import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import { orderV2QueryKeys } from '../api/queryKeys';

type VerificationResponse = { verification_enabled?: boolean };

// Mirrors legacy CreateGigOrder: read the customer's QR check-in flag to
// decide whether to expose the toggle on this order, and seed the
// default. The hook does not own state — it pushes the result up via
// `onResult` so the orchestrator can fold it into the wizard's
// `verificationVisible` / `verificationEnabled` slices. The
// orchestrator's `setVerificationVisible` action re-checks
// `prev.isPrefilled` so a template's saved value isn't clobbered.
const useVerificationFlag = (
  customerId: number | null,
  onResult: (visible: boolean, defaultEnabled: boolean) => void,
) => {
  const enabled = customerId != null;
  const query = useQuery<{ enabled: boolean } | null>({
    queryKey: enabled
      ? orderV2QueryKeys.verificationFlag(customerId)
      : ['orders_v2', 'verification_flag', 'disabled'],
    queryFn: async () => {
      const response = await axiosRubyClient.get(
        `/api/customers/${customerId}`,
      );
      const body = response?.data as VerificationResponse | undefined;
      return { enabled: body?.verification_enabled === true };
    },
    enabled,
  });

  // When customerId is null, signal "not visible". When the fetch
  // succeeds, signal `(enabled, enabled)` mirroring legacy. On error,
  // fall back to `(false, false)` (legacy behavior on rejection).
  useEffect(() => {
    if (!enabled) {
      onResult(false, false);
      return;
    }
    if (query.error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch verification_enabled:', query.error);
      onResult(false, false);
      return;
    }
    if (query.data) {
      onResult(query.data.enabled, query.data.enabled);
    }
  }, [enabled, query.data, query.error, onResult]);
};

export default useVerificationFlag;
